import { useTranslation } from 'react-i18next';

import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import Badge from '../../../../../../shared/components/Globals/Badge/Badge';
import Spinner from '../../../../../../shared/components/Globals/Spinner/Spinner';
import Table from '../../../../../../shared/components/Globals/Table/Table';
import { CustomModal } from '../../../../../../shared/components/Modals/CustomModal/CustomModal';
import { usePlanBenefits } from '../../../../../../shared/hooks/usePlanBenefits/usePlanBenefits';
import type { IPlanDetailsModalProps } from '../../../../../../shared/interfaces/clients';
import { TRANSLATIONS_CLIENTS } from '../../../../../../shared/translations/clients';
import './PlanDetailsModal.scss';

const PLAN_DETAILS = TRANSLATIONS_CLIENTS.profile.planDetails;

const EMPTY_VALUE = '—';

const PlanDetailsModal = ({
  open,
  plan,
  onClose,
  statusBadge,
  effectiveDate,
  renewalDate,
  renewalUrgent
}: IPlanDetailsModalProps) => {
  const { t } = useTranslation();
  const { planCoverage, voluntary, loading } = usePlanBenefits(plan.addonIds);

  const hasStats = Boolean(effectiveDate) || Boolean(renewalDate);

  const title = t(PLAN_DETAILS.title, { planName: plan.name });

  const renewalValueClass = [
    'plan-details-modal__stat-value',
    renewalUrgent ? 'plan-details-modal__stat-value--urgent' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <CustomModal
      open={open}
      onBackdropClick={onClose}
      title={
        <span className="plan-details-modal__title">
          <span className="plan-details-modal__title-text">{title}</span>
          {statusBadge && <Badge variant={statusBadge.variant}>{t(statusBadge.labelKey)}</Badge>}
        </span>
      }
      size="xl"
      customClass="plan-details-modal"
    >
      <div className="plan-details-modal__body">
        {hasStats && (
          <div className="plan-details-modal__stats">
            {effectiveDate && (
              <div className="plan-details-modal__stat">
                <span className="plan-details-modal__stat-label">{t(PLAN_DETAILS.fields.effectiveDate)}</span>
                <span className="plan-details-modal__stat-value">{effectiveDate}</span>
              </div>
            )}

            {renewalDate && (
              <div className="plan-details-modal__stat">
                <span className="plan-details-modal__stat-label">{t(PLAN_DETAILS.fields.renewalDate)}</span>
                <span className={renewalValueClass}>{renewalDate}</span>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="plan-details-modal__loading">
            <Spinner size={20} />
          </div>
        )}

        {!loading && planCoverage.length > 0 && (
          <section className="plan-details-modal__section">
            <h4 className="plan-details-modal__section-title">{t(PLAN_DETAILS.sections.planCoverage)}</h4>
            <div className="plan-details-modal__coverage">
              {planCoverage.map((benefit) => (
                <div key={benefit.id} className="plan-details-modal__anchor">
                  <span className="plan-details-modal__anchor-name">{benefit.name}</span>
                  {benefit.detail && <span className="plan-details-modal__anchor-detail">{benefit.detail}</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        {!loading && voluntary.length > 0 && (
          <section className="plan-details-modal__section">
            <h4 className="plan-details-modal__section-title">{t(PLAN_DETAILS.sections.voluntaryBenefits)}</h4>
            <div className="plan-details-modal__voluntary">
              <Table density="md">
                <Table.Head>
                  <Table.Row>
                    <Table.HeaderCell>{t(PLAN_DETAILS.table.benefit)}</Table.HeaderCell>
                    <Table.HeaderCell>{t(PLAN_DETAILS.table.option)}</Table.HeaderCell>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {voluntary.map((benefit) => (
                    <Table.Row key={benefit.id}>
                      <Table.Cell className="plan-details-modal__voluntary-name">{benefit.name}</Table.Cell>
                      <Table.Cell className={benefit.detail ? undefined : 'plan-details-modal__voluntary-empty'}>
                        {benefit.detail || EMPTY_VALUE}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </section>
        )}
      </div>

      <div className="plan-details-modal__footer">
        <CustomButton variant="default" size="m" onClick={onClose}>
          {t(PLAN_DETAILS.actions.close)}
        </CustomButton>
      </div>
    </CustomModal>
  );
};

export default PlanDetailsModal;
